import React, { useEffect, useState, useContext, useRef } from 'react';
import { Box, Typography, Container, useMediaQuery, useTheme } from '@mui/material';
import mixpanel from 'mixpanel-browser';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import DisplayHandout from './display-handout';
import { useParams, useLocation } from 'react-router-dom';
import { useHandoutData } from './handout-data-context';
import HandoutActions from './handout-actions';
import AuthorsAndReferences from './authors-and-references';
import WhiteboardToolbar from './whiteboard/WhiteboardToolbar';
import SaveWhiteboardModal from './whiteboard/save-whiteboard-modal';
import LoadWhiteboardModal from './whiteboard/load-whiteboard-modal';
import UserApi from '../../../rest-api/user-api';
import BlockingSpinner from '../../UIControls/BlockingSpinner';
import { useSnackbar } from '../SnackbarProvider';
import { scaleLoadedObjects } from './whiteboard/scaleLoadedObjects';
import useIsLandscape from '../../UIControls/useIsLandscape';
import { useUnsavedChanges } from '../UnsavedChangesContext';
import { CustomiseModeProvider, useCustomiseMode } from './customise-mode/CustomiseModeContext';
import SaveCustomisedHandoutModal from './customise-mode/SaveCustomisedHandoutModal';
import SaveRegularHandoutModal from './SaveRegularHandoutModal';
import CTAUpgradeDialog from '../../UIControls/CTAUpgradeDialog';
import { useNavigate } from 'react-router-dom';
import UploadUtils from '../../../utils/UploadUtils';
import UploadPracticeLogoModal from '../UploadPracticeLogoModal';
import { getCTAUpgradeImageUrl } from '../../../config';
import { hasPendingCustomisedRestoreForHandout } from '../../../utils/pending-upgrade-restore-utils';

function HandoutViewer() {
    const location = useLocation();
    const navigate = useNavigate();
    const { id: contentId } = useParams();
    const {
        hasUnsavedChanges,
        showPrompt,
        setHasUnsavedChanges,
        promptBeforeAction,
        setOnSave,
        setOnDiscard,
        onCancelNavigation,
    } = useUnsavedChanges();
    const { contentStore, userDataStore, authStore, whiteboardStore, customisedHandoutStore, savedHandoutStore } =
        useContext(StoreContext);
    const { handoutData, setHandoutData } = useHandoutData();
    const theme = useTheme();
    const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [showAuthorsAndReferences, setShowAuthorsAndReferences] = useState(false);
    const [showLabels, setShowLabels] = useState(true);
    const [isDiagramOnly, setIsDiagramOnly] = useState(false);
    const [is3DModelOnly, setIs3DModelOnly] = useState(false);
    const [showWhiteboard, setShowWhiteboard] = useState(false);
    const [isAutoLoadingWhiteboard, setIsAutoLoadingWhiteboard] = useState(false);
    const [isToolbarExpanded, setIsToolbarExpanded] = useState(false);

    const [activeTool, setActiveTool] = useState('pencil');
    const [color, setColor] = useState('#000000');
    const [thickness, setThickness] = useState(2);
    const activeColor = activeTool === 'highlighter' ? 'rgba(220,240,60,0.5)' : color;
    const canvasRef = useRef(null);
    const suppressUnsavedChangesRef = useRef(false);
    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
    const [isLoadModalOpen, setIsLoadModalOpen] = useState(false);
    const showSnackbar = useSnackbar();
    const handoutTopRef = useRef(null);

    // set if they should SHOW
    const [show3DModel, setShow3DModel] = useState(true);
    const [showMainIllustration, setShowMainIllustration] = useState(true);
    const [showTutorialVideo, setShowTutorialVideo] = useState(true);

    // State variables to check if the handout contains the sections like 3D, or diagram
    const [has3DModel, setHas3DModel] = useState(false);
    const [hasMainIllustration, setHasMainIllustration] = useState(false);
    const [hasTutorialVideo, setHasTutorialVideo] = useState(false);
    const MAX_RECENT_HANDOUTS = 5;

    // disclaimer
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [disclaimerVetName, setDisclaimerVetName] = useState('');
    const [disclaimerClinicName, setDisclaimerClinicName] = useState('');

    // customise mode
    const {
        isCustomiseMode,
        setIsCustomiseMode,
        initializeCustomiseMode,
        loadCustomisedContentInViewMode,
        loadCustomisedContentForEdit,
        exitCustomiseMode,
        markAsLoadedCustomisedHandout,
        clearCustomiseMode,
        editedContentData,
        editedContentHandoutId,
        getEditedContentData,
        setWhiteboardIllustration,
        selectedWhiteboard, // Bug Fix: Need this to check if CH has attached WB
        // Phase 1: Combined mode support
        hasActiveWhiteboardDrawings,
        setHasActiveWhiteboardDrawings,
        activeCanvasRef,
        setActiveCanvasRef,
    } = useCustomiseMode();
    const [isSaveCustomisedHandoutModalOpen, setIsSaveCustomisedHandoutModalOpen] = useState(false);
    const [isSaveRegularHandoutModalOpen, setIsSaveRegularHandoutModalOpen] = useState(false);
    const [activeSavedHandoutContext, setActiveSavedHandoutContext] = useState(null);
    const [activeCustomisedHandoutContext, setActiveCustomisedHandoutContext] = useState(null);
    const [upgradeCTAOpen, setUpgradeCTAOpen] = useState(false);
    const [upgradeClinicCTAOpen, setUpgradeClinicCTAOpen] = useState(false);
    const [logoModalOpen, setLogoModalOpen] = useState(false);
    const [selectedLogoFile, setSelectedLogoFile] = useState(null);
    const [isSavingLogo, setIsSavingLogo] = useState(false);
    const [isMobileToolbarVisible, setIsMobileToolbarVisible] = useState(true);

    // Phase 1: Combined mode detection - when both WB and CH are active
    const isCombinedMode = showWhiteboard && isCustomiseMode;

    // check if loaded from My library
    const autoloadFromLibrary = sessionStorage.getItem('autoloadWhiteboardFromLibrary') === 'true';

    // last colours used
    const [lastUsedColours, setLastUsedColours] = useState({ pencil: [], spray: [] });

    const isLandscape = useIsLandscape();

    // Track previous/current contentId to detect changes and cleanup correctly
    const prevContentIdRef = useRef(contentId);
    const currentContentIdRef = useRef(contentId);
    const skipUnmountCleanupRef = useRef(true);
    const isRestoringPendingSaveRef = useRef(false); // Flag to prevent cleanup during restoration
    const initialSelectedWhiteboardIdRef = useRef(null);

    // Track last loaded customised handout ID AND contentId combination to prevent duplicate loads
    const lastLoadedCustomisedHandoutRef = useRef({ contentId: null, customisedHandoutId: null });
    const PENDING_UPGRADE_HANDOUT_ID_KEY = 'pendingUpgradeHandoutId';
    const PENDING_UPGRADE_HANDOUT_ID_SET_AT_KEY = 'pendingUpgradeHandoutIdSetAt';
    const setPendingUpgradeHandoutId = (handoutId) => {
        if (!handoutId) return;
        sessionStorage.setItem(PENDING_UPGRADE_HANDOUT_ID_KEY, handoutId);
        sessionStorage.setItem(PENDING_UPGRADE_HANDOUT_ID_SET_AT_KEY, String(Date.now()));
    };
    const clearPendingUpgradeHandoutId = () => {
        sessionStorage.removeItem(PENDING_UPGRADE_HANDOUT_ID_KEY);
        sessionStorage.removeItem(PENDING_UPGRADE_HANDOUT_ID_SET_AT_KEY);
    };

    // TODO: not sure if we need this?
    // const formatDate = (timestamp) => {
    //     if (!timestamp) return 'Date not available'; // Handle null timestamp
    //     const date = new Date(timestamp);
    //     return date.toLocaleDateString(undefined, {
    //         year: 'numeric',
    //         month: 'long',
    //         day: 'numeric',
    //     });
    // };

    // Reset handoutData when contentId changes (navigating to different handout)
    useEffect(() => {
        if (prevContentIdRef.current !== contentId) {
            console.log(
                '[HandoutViewer] ContentId changed, resetting handoutData. Current: ',
                prevContentIdRef.current,
                ' contentId: ',
                contentId,
            );
            try {
                const pendingWhiteboardSave = localStorage.getItem('pendingWhiteboardSave');
                if (pendingWhiteboardSave) {
                    const data = JSON.parse(pendingWhiteboardSave);
                    if (data?.handoutId && data.handoutId !== contentId) {
                        localStorage.removeItem('pendingWhiteboardSave');
                        clearPendingUpgradeHandoutId();
                    }
                }
            } catch (error) {
                console.error('[HandoutViewer] Failed to clear pendingWhiteboardSave:', error);
                localStorage.removeItem('pendingWhiteboardSave');
                clearPendingUpgradeHandoutId();
            }

            try {
                const pendingCustomisedSave = localStorage.getItem('pendingCustomisedHandoutSave');
                if (pendingCustomisedSave) {
                    const data = JSON.parse(pendingCustomisedSave);
                    if (data?.handoutId && data.handoutId !== contentId) {
                        const previousContentId = prevContentIdRef.current;
                        const isLeavingPendingHandout = previousContentId && data.handoutId === previousContentId;
                        const isRecent = Date.now() - data.timestamp < 3600000;
                        if (isLeavingPendingHandout || !isRecent) {
                            localStorage.removeItem('pendingCustomisedHandoutSave');
                            sessionStorage.removeItem('forcePendingCustomisedHandoutId');
                            clearPendingUpgradeHandoutId();
                        }
                    }
                }
            } catch (error) {
                console.error('[HandoutViewer] Failed to clear pendingCustomisedHandoutSave:', error);
                localStorage.removeItem('pendingCustomisedHandoutSave');
                clearPendingUpgradeHandoutId();
            }

            try {
                const pendingCombinedSave = localStorage.getItem('pendingCombinedHandoutSave');
                if (pendingCombinedSave) {
                    const data = JSON.parse(pendingCombinedSave);
                    if (data?.handoutId && data.handoutId !== contentId) {
                        localStorage.removeItem('pendingCombinedHandoutSave');
                        clearPendingUpgradeHandoutId();
                    }
                }
            } catch (error) {
                console.error('[HandoutViewer] Failed to clear pendingCombinedHandoutSave:', error);
                localStorage.removeItem('pendingCombinedHandoutSave');
                clearPendingUpgradeHandoutId();
            }
            clearCustomiseMode();
            setHandoutData(null);
            setActiveSavedHandoutContext(null);
            setActiveCustomisedHandoutContext(null);
            prevContentIdRef.current = contentId;
            currentContentIdRef.current = contentId;
            // Reset the last loaded CH ref when contentId changes
            lastLoadedCustomisedHandoutRef.current = { contentId: null, customisedHandoutId: null };
        }
    }, [contentId, setHandoutData]);

    useEffect(() => {
        // Skip cleanup if we're in the middle of restoring a pending save
        if (isRestoringPendingSaveRef.current) {
            return;
        }

        if (editedContentHandoutId && editedContentHandoutId !== contentId) {
            clearCustomiseMode();
            return;
        }
        if (editedContentData && !editedContentHandoutId) {
            clearCustomiseMode();
        }
    }, [clearCustomiseMode, contentId, editedContentData, editedContentHandoutId]);

    useEffect(() => {
        return () => {
            if (skipUnmountCleanupRef.current) {
                skipUnmountCleanupRef.current = false;
                return;
            }
            sessionStorage.removeItem('forcePendingCustomisedHandoutId');
        };
    }, []);

    useEffect(() => {
        console.log('[HandoutViewer] CustomiseMode context available:', isCustomiseMode);
    }, [isCustomiseMode]);

    useEffect(() => {
        initialSelectedWhiteboardIdRef.current = null;
    }, [contentId]);

    useEffect(() => {
        if (!handoutData || !savedHandoutStore) return;

        const savedHandoutId = localStorage.getItem('loadSavedHandout');
        if (!savedHandoutId) {
            setActiveSavedHandoutContext(null);
            return;
        }

        localStorage.removeItem('loadSavedHandout');

        const loadSavedHandoutContext = async () => {
            try {
                const savedRecord = await savedHandoutStore.getSavedHandoutById(savedHandoutId);
                if (!savedRecord) {
                    setActiveSavedHandoutContext(null);
                    return;
                }
                if (savedRecord.baseContentId !== contentId) {
                    setActiveSavedHandoutContext(null);
                    return;
                }
                initialSelectedWhiteboardIdRef.current = null;
                setActiveSavedHandoutContext(savedRecord);
            } catch (error) {
                console.error('[AutoLoad Saved] Failed to load saved handout context:', error);
            }
        };

        loadSavedHandoutContext();
    }, [handoutData, savedHandoutStore, contentId]);

    // Listen for PDF generation completion messages from child window
    useEffect(() => {
        const handleMessage = (event) => {
            // Verify message origin for security
            if (event.origin !== window.location.origin) return;

            // Check if this is a PDF generation success message
            if (event.data && event.data.type === 'PDF_GENERATED') {
                // Show success message for 10 seconds to ensure user sees it
                showSnackbar('PDF created and downloaded successfully', 'success', 10000);
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [showSnackbar]);

    useEffect(() => {
        setOnSave(() => () => {
            // Check if user is on Free or Student plan - show upgrade dialog instead of save modal
            if (
                userDataStore?.subscriptionData?.accountType === 'Free' ||
                userDataStore?.subscriptionData?.accountType === 'Student'
            ) {
                setUpgradeCTAOpen(true);
                return;
            }
            // Open the appropriate save modal based on current mode
            // If customise mode is active (alone or combined with WB), use SaveCustomisedHandoutModal
            // Otherwise, if only whiteboard is active, use SaveWhiteboardModal
            if (isCustomiseMode) {
                setIsSaveCustomisedHandoutModalOpen(true);
            } else if (showWhiteboard) {
                setIsSaveModalOpen(true);
            } else {
                setIsSaveRegularHandoutModalOpen(true);
            }
        });
        setOnDiscard(() => () => {
            clearCustomiseMode();
        });
        return () => {
            setOnSave(null);
            setOnDiscard(null);
        };
    }, [
        clearCustomiseMode,
        setOnDiscard,
        setOnSave,
        userDataStore?.subscriptionData?.accountType,
        isCustomiseMode,
        showWhiteboard,
    ]);

    // Check for pending whiteboard saves after user upgrades from Free plan
    useEffect(() => {
        const accountType = userDataStore?.subscriptionData?.accountType;
        if (!accountType || accountType === 'Free') return;

        const pendingCombinedSave = localStorage.getItem('pendingCombinedHandoutSave');
        if (pendingCombinedSave) return;

        const pendingSave = localStorage.getItem('pendingWhiteboardSave');
        if (!pendingSave) return;

        let saveData = null;
        try {
            saveData = JSON.parse(pendingSave);
        } catch (error) {
            console.error('Error parsing pending whiteboard save:', error);
            localStorage.removeItem('pendingWhiteboardSave');
            return;
        }

        const isRecent = !saveData.timestamp || Date.now() - saveData.timestamp < 3600000; // 1 hour
        if (!isRecent) {
            localStorage.removeItem('pendingWhiteboardSave');
            return;
        }

        const isCurrentHandout = saveData.handoutId === contentId;
        if (!isCurrentHandout) {
            return;
        }

        const waitForCanvasReady = (maxAttempts = 10, delay = 100) =>
            new Promise((resolve, reject) => {
                let attempts = 0;
                const check = () => {
                    const canvas = canvasRef.current;
                    if (canvas && canvas.getWidth() > 10 && canvas.getHeight() > 10) {
                        return resolve(canvas);
                    }
                    attempts += 1;
                    if (attempts >= maxAttempts) {
                        return reject(new Error('Canvas not ready in time'));
                    }
                    setTimeout(check, delay);
                };
                check();
            });

        const restorePendingSave = async () => {
            try {
                const jsonData = typeof saveData.canvasData === 'string' ? JSON.parse(saveData.canvasData) : null;
                if (!jsonData) {
                    console.warn('Pending whiteboard save missing canvas data.');
                    localStorage.removeItem('pendingWhiteboardSave');
                    return;
                }

                if (saveData.showLabels !== undefined) {
                    setShowLabels(!!saveData.showLabels);
                }
                setShowWhiteboard(true);

                await waitForCanvasReady();

                const oldW = jsonData.canvasWidth || 0;
                const oldH = jsonData.canvasHeight || 0;
                const newW = canvasRef.current.getWidth();
                const newH = canvasRef.current.getHeight();

                canvasRef.current.loadFromJSON(jsonData, () => {
                    setTimeout(() => {
                        if (!canvasRef.current) return;

                        if (typeof canvasRef.current.doScaleAndResize === 'function') {
                            canvasRef.current.doScaleAndResize();
                        }

                        const updatedW = canvasRef.current.getWidth();
                        const updatedH = canvasRef.current.getHeight();

                        if (canvasRef.current.getObjects().length > 0) {
                            scaleLoadedObjects(canvasRef.current, oldW, oldH, updatedW, updatedH);
                        }

                        canvasRef.current.requestRenderAll();
                        canvasRef.current.calcOffset();
                        window.dispatchEvent(new Event('resize'));

                        canvasRef.current.getObjects().forEach((obj) => {
                            obj.setCoords();
                        });

                        canvasRef.current.renderAll();

                        if (saveData.lastUsedColours) {
                            setLastUsedColours(saveData.lastUsedColours);
                        }

                        setHasUnsavedChanges(true);
                        showSnackbar('Your whiteboard has been preserved. Click Save to complete saving it.', 'info');

                        localStorage.removeItem('pendingWhiteboardSave');
                    }, 600);
                });
            } catch (error) {
                console.error('Error recovering pending whiteboard save:', error);
                localStorage.removeItem('pendingWhiteboardSave');
            }
        };

        restorePendingSave();
    }, [userDataStore?.subscriptionData?.accountType, contentId, showSnackbar]);

    // Check for combined pending saves (customised handout + whiteboard)
    useEffect(() => {
        const accountType = userDataStore?.subscriptionData?.accountType;
        if (!accountType || accountType === 'Free') return;

        const pendingSave = localStorage.getItem('pendingCombinedHandoutSave');
        if (!pendingSave) return;

        let saveData = null;
        try {
            saveData = JSON.parse(pendingSave);
        } catch (error) {
            console.error('[Combined Restore] Error parsing pendingCombinedHandoutSave:', error);
            localStorage.removeItem('pendingCombinedHandoutSave');
            return;
        }

        const isRecent = !saveData.timestamp || Date.now() - saveData.timestamp < 3600000; // 1 hour
        if (!isRecent) {
            console.warn('[Combined Restore] Pending save expired', { timestamp: saveData.timestamp });
            localStorage.removeItem('pendingCombinedHandoutSave');
            return;
        }

        const isCurrentHandout = saveData.handoutId === contentId;
        if (!isCurrentHandout) {
            console.log('[Combined Restore] Pending save for different handout', {
                pendingHandoutId: saveData.handoutId,
                contentId,
            });
            return;
        }

        const waitForCanvasReady = (maxAttempts = 10, delay = 100) =>
            new Promise((resolve, reject) => {
                let attempts = 0;
                const check = () => {
                    const canvas = canvasRef.current;
                    if (canvas && canvas.getWidth() > 10 && canvas.getHeight() > 10) {
                        return resolve(canvas);
                    }
                    attempts += 1;
                    if (attempts >= maxAttempts) {
                        return reject(new Error('Canvas not ready in time'));
                    }
                    setTimeout(check, delay);
                };
                check();
            });

        const restoreCombinedSave = async () => {
            try {
                console.log('[Combined Restore] Restoring pending save', saveData);

                // Set flag to prevent cleanup during restoration
                isRestoringPendingSaveRef.current = true;

                if (saveData.editedContentData) {
                    loadCustomisedContentForEdit(saveData.editedContentData, 'en-UK', saveData.handoutId);
                }

                if (saveData.selectedWhiteboard) {
                    setWhiteboardIllustration(saveData.selectedWhiteboard);
                }

                if (saveData.whiteboard?.showLabels !== undefined) {
                    setShowLabels(!!saveData.whiteboard.showLabels);
                }

                if (saveData.whiteboard?.canvasData) {
                    const jsonData =
                        typeof saveData.whiteboard.canvasData === 'string'
                            ? JSON.parse(saveData.whiteboard.canvasData)
                            : null;
                    if (jsonData) {
                        setShowWhiteboard(true);
                        await waitForCanvasReady();

                        const oldW = jsonData.canvasWidth || 0;
                        const oldH = jsonData.canvasHeight || 0;

                        canvasRef.current.loadFromJSON(jsonData, () => {
                            setTimeout(() => {
                                if (!canvasRef.current) return;

                                if (typeof canvasRef.current.doScaleAndResize === 'function') {
                                    canvasRef.current.doScaleAndResize();
                                }

                                const updatedW = canvasRef.current.getWidth();
                                const updatedH = canvasRef.current.getHeight();

                                if (canvasRef.current.getObjects().length > 0) {
                                    scaleLoadedObjects(canvasRef.current, oldW, oldH, updatedW, updatedH);
                                }

                                canvasRef.current.requestRenderAll();
                                canvasRef.current.calcOffset();
                                window.dispatchEvent(new Event('resize'));

                                canvasRef.current.getObjects().forEach((obj) => {
                                    obj.setCoords();
                                });

                                canvasRef.current.renderAll();
                            }, 600);
                        });
                    }
                }

                if (saveData.whiteboard?.lastUsedColours) {
                    setLastUsedColours(saveData.whiteboard.lastUsedColours);
                }

                setHasUnsavedChanges(true);
                showSnackbar(
                    'Your handout and whiteboard have been preserved. Click Save to complete saving it.',
                    'info',
                );
            } catch (error) {
                console.error('[Combined Restore] Error recovering pending save:', error);
            } finally {
                // Clear flag after restoration completes
                isRestoringPendingSaveRef.current = false;
            }

            localStorage.removeItem('pendingCombinedHandoutSave');
            localStorage.removeItem('pendingWhiteboardSave');
            localStorage.removeItem('pendingCustomisedHandoutSave');
        };

        restoreCombinedSave();
    }, [userDataStore?.subscriptionData?.accountType, contentId, showSnackbar]);

    // Check for pending customised handout saves after user upgrades from Free plan
    useEffect(() => {
        const accountType = userDataStore?.subscriptionData?.accountType;
        if (!accountType || accountType === 'Free') return;

        const pendingCombinedSave = localStorage.getItem('pendingCombinedHandoutSave');
        if (pendingCombinedSave) return;

        const pendingSave = localStorage.getItem('pendingCustomisedHandoutSave');
        if (!pendingSave) {
            console.log('[Customise Restore] No pendingCustomisedHandoutSave');
            return;
        }

        let saveData = null;
        try {
            saveData = JSON.parse(pendingSave);
        } catch (error) {
            console.error('[Customise Restore] Error parsing pendingCustomisedHandoutSave:', error);
            localStorage.removeItem('pendingCustomisedHandoutSave');
            return;
        }

        const isRecent = !saveData.timestamp || Date.now() - saveData.timestamp < 3600000; // 1 hour
        if (!isRecent) {
            console.warn('[Customise Restore] Pending save expired', { timestamp: saveData.timestamp });
            localStorage.removeItem('pendingCustomisedHandoutSave');
            return;
        }

        const isCurrentHandout = saveData.handoutId === contentId;
        if (!isCurrentHandout) {
            const forceHandoutId = sessionStorage.getItem('forcePendingCustomisedHandoutId');
            if (forceHandoutId && forceHandoutId === saveData.handoutId) {
                sessionStorage.removeItem('forcePendingCustomisedHandoutId');
                navigate(`/handout/${saveData.handoutId}`);
            }
            console.log('[Customise Restore] Pending save for different handout', {
                pendingHandoutId: saveData.handoutId,
                contentId,
            });
            return;
        }
        sessionStorage.removeItem('forcePendingCustomisedHandoutId');

        try {
            console.log('[Customise Restore] Restoring pending save', saveData);

            // Set flag to prevent cleanup during restoration
            isRestoringPendingSaveRef.current = true;

            if (saveData.editedContentData) {
                const locale = 'en-UK';
                const localeData = saveData.editedContentData?.[locale] || saveData.editedContentData;
                if (Array.isArray(localeData)) {
                    initializeCustomiseMode(localeData, locale, saveData.handoutId);
                } else {
                    loadCustomisedContentForEdit(saveData.editedContentData, locale, saveData.handoutId);
                }
                setIsCustomiseMode(true);
            }

            if (saveData.selectedWhiteboard) {
                setWhiteboardIllustration(saveData.selectedWhiteboard);
            }

            setHasUnsavedChanges(true);
            showSnackbar('Your customised handout has been preserved. Click Save to complete saving it.', 'info');
            if (saveData.timestamp) {
                sessionStorage.setItem('pendingCustomisedPromptDismissedAt', String(saveData.timestamp));
            }
        } catch (error) {
            console.error('[Customise Restore] Error recovering pending save:', error);
        } finally {
            // Clear flag after restoration completes
            isRestoringPendingSaveRef.current = false;
        }
    }, [userDataStore?.subscriptionData?.accountType, contentId, showSnackbar]);

    useEffect(() => {
        if (!handoutData) {
            // Clear any existing canvas when contentId changes to prevent overlapping
            if (canvasRef.current) {
                console.log('[HandoutViewer] Clearing canvas due to contentId change');
                canvasRef.current.clear();
                canvasRef.current.dispose();
                canvasRef.current = null;
            }

            // console.log(`Fetching content data for contentId: ${contentId}`);
            const fetchedData = contentStore.getContentFromIds([contentId]);
            if (fetchedData && fetchedData.length > 0) {
                // console.log('fetchedData: ', fetchedData);
                setHandoutData(fetchedData[0]);

                // ALWAYS clear customised handout state when loading a regular handout
                // This prevents sharing regular handouts with stale customised handout data
                localStorage.removeItem('lastCustomisedHandoutState');

                // Clear in-memory customise mode context unless this is an explicit load
                // from My Workspace (which sets loadCustomisedHandout in localStorage first)
                // or a post-upgrade customised handout restore for this handout.
                // prevContentIdRef is initialised to contentId, so the contentId-change effect
                // never fires on remount for the same handout — this is the only safe place to
                // flush stale editedContentData left over from a previous save session.
                const isPendingCustomisedRestoreForCurrentHandout = hasPendingCustomisedRestoreForHandout({
                    contentId,
                    forcePendingCustomisedHandoutId: sessionStorage.getItem('forcePendingCustomisedHandoutId'),
                    pendingCustomisedHandoutSave: localStorage.getItem('pendingCustomisedHandoutSave'),
                    pendingCombinedHandoutSave: localStorage.getItem('pendingCombinedHandoutSave'),
                    onParseError: (message, error) => {
                        console.error(message, error);
                    },
                });

                if (!localStorage.getItem('loadCustomisedHandout') && !isPendingCustomisedRestoreForCurrentHandout) {
                    clearCustomiseMode();
                } else if (sessionStorage.getItem('forcePendingCustomisedHandoutId') === contentId) {
                    sessionStorage.removeItem('forcePendingCustomisedHandoutId');
                }

                // Check if there's a whiteboard that should be loaded
                // If yes, don't clear the disclaimer (whiteboard will set it)
                const hasWhiteboardState =
                    localStorage.getItem('lastWhiteboardState') ||
                    sessionStorage.getItem('whiteboardVetName') ||
                    sessionStorage.getItem('whiteboardClinicName');

                // Only clear disclaimer if there's no whiteboard state
                // (If whiteboard exists, it will set the disclaimer itself)
                if (!hasWhiteboardState) {
                    setShowDisclaimer(false);
                    setDisclaimerVetName('');
                    setDisclaimerClinicName('');
                }
            } else {
                console.log('failed to fetch data');
            }
        } else {
            const locale = 'en-UK'; // TODO: this might be put higher (will be changed later anyway)
            const localeData = handoutData.contentData[locale] || [];
            // console.log('Locale Data:', localeData);

            // Extract section names
            const sectionNames = localeData.map((section) => section.sectionName);

            // Check for 3D model
            const has3D = localeData.some((section) => section.blocks.some((block) => block.type === '3dModel'));
            setHas3DModel(has3D);

            // Check for Main Illustration (looking for 'illustration' blocks)
            const hasIllustration = localeData.some((section) =>
                section.blocks.some((block) => block.type === 'illustration'),
            );
            setHasMainIllustration(hasIllustration);

            // Check for Tutorial Video - THIS IS ONLY MOCK UP - as we do not have it yet
            const hasVideo = localeData.some(
                (section) => section.blocks.some((block) => block.type === 'tutorialVideo'), // this will need to be changed
            );
            setHasTutorialVideo(hasVideo);

            // check if that's only diagram (stand alone) and 3D model
            const isOnlyDiagram =
                sectionNames.length === 2 && sectionNames.includes('title') && sectionNames.includes('diagram');
            setIsDiagramOnly(isOnlyDiagram);

            const isOnly3DModel = sectionNames.length === 2 && sectionNames.includes('title') && has3D;
            setIs3DModelOnly(isOnly3DModel);
        }

        updateRecentHandouts(contentId);

        // Seed recent colours from user (multiuser-aware); be aware - it's "includes" multiuser
        const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');

        let seed = { pencil: [], spray: [] };
        if (isMultiUser) {
            const me = userDataStore?.multiuserData?.multiuserUsers?.find(
                (u) =>
                    u.emailAddress === userDataStore?.userData?.emailAddress &&
                    u.inviteId === userDataStore?.userData?.inviteId,
            );
            // if (me?.lastUsedColours) seed = me.lastUsedColours;
            seed =
                me?.lastUsedColours ??
                userDataStore?.lastUsedColours ??
                userDataStore?.userData?.lastUsedColours ??
                seed;
        } else {
            seed = userDataStore?.lastUsedColours || userDataStore?.userData?.lastUsedColours || seed;
        }

        setLastUsedColours((prev) => {
            const alreadyHasData = (prev?.pencil?.length || 0) > 0 || (prev?.spray?.length || 0) > 0;

            if (alreadyHasData) return prev; // don't overwrite if already set

            return {
                pencil: seed?.pencil || [],
                spray: seed?.spray || [],
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentId, contentStore, handoutData, setHandoutData]);

    useEffect(() => {
        const lastWhiteboardState = localStorage.getItem('lastWhiteboardState');
        if (!lastWhiteboardState) return;

        const parsed = JSON.parse(lastWhiteboardState);

        if (!autoloadFromLibrary) {
            localStorage.removeItem('lastWhiteboardState'); // prevent reloading on refresh
        }

        if (autoloadFromLibrary) {
            sessionStorage.removeItem('autoloadWhiteboardFromLibrary');
        }

        if (!parsed.handoutId || parsed.handoutId !== contentId || !parsed.canvasJsonLink) {
            console.warn('[AutoLoad] Skipping due to invalid or mismatched whiteboard data.');
            return;
        }

        // Show disclaimer if values exist in sessionStorage
        const vetName = sessionStorage.getItem('whiteboardVetName');
        const clinicName = sessionStorage.getItem('whiteboardClinicName');

        if (vetName || clinicName) {
            setDisclaimerVetName(vetName || '');
            setDisclaimerClinicName(clinicName || '');
            setShowDisclaimer(true);

            // Clean up
            sessionStorage.removeItem('whiteboardVetName');
            sessionStorage.removeItem('whiteboardClinicName');
        }

        console.warn('[AutoLoad] Detected whiteboard to load:', parsed);

        const isDiagramVariant = (val) => ['withLabels', 'noLabels'].includes(val);
        if (isDiagramVariant(parsed.diagramVariant)) {
            setShowLabels(parsed.diagramVariant === 'withLabels');
        }

        setShowWhiteboard(true);

        if (parsed.whiteboardId) {
            applyWhiteboardColours(parsed.whiteboardId);
        }

        const waitForCanvasReady = (maxAttempts = 10, delay = 100) =>
            new Promise((resolve, reject) => {
                let attempts = 0;
                const check = () => {
                    const canvas = canvasRef.current;
                    if (canvas && canvas.getWidth() > 10 && canvas.getHeight() > 10) {
                        return resolve(canvas);
                    }

                    attempts++;
                    if (attempts >= maxAttempts) {
                        return reject('Canvas not ready in time');
                    }

                    setTimeout(check, delay);
                };
                check();
            });

        const loadWhiteboard = async () => {
            setIsAutoLoadingWhiteboard(true);
            try {
                // Always generate fresh presigned URL to avoid expiry issues
                const payload = {
                    contentCategory: 'whiteboard',
                    files: [{ method: 'get', key: parsed.canvasJsonLink }],
                };

                const presignedResp = await UserApi.generatePresignedUrls(authStore.idToken, payload);
                const presignedUrl = presignedResp.getUrls?.[0]?.presignedUrl;

                if (!presignedUrl) throw new Error('No presigned URL returned');

                const response = await fetch(presignedUrl);
                if (!response.ok) throw new Error('Failed to fetch whiteboard JSON');

                const jsonData = await response.json();

                await waitForCanvasReady(); // ensures canvas is fully mounted + sized

                suppressUnsavedChangesRef.current = true;
                canvasRef.current.loadFromJSON(jsonData, () => {
                    setTimeout(() => {
                        if (!canvasRef.current) {
                            suppressUnsavedChangesRef.current = false;
                            return;
                        }

                        // Force a resize (in case dimensions changed after canvas mounted)
                        if (typeof canvasRef.current.doScaleAndResize === 'function') {
                            canvasRef.current.doScaleAndResize();
                        }

                        const oldW = jsonData.canvasWidth || 0;
                        const oldH = jsonData.canvasHeight || 0;
                        const newW = canvasRef.current.getWidth();
                        const newH = canvasRef.current.getHeight();

                        const objects = canvasRef.current.getObjects();

                        if (objects.length > 0 && oldW && oldH && newW && newH) {
                            scaleLoadedObjects(canvasRef.current, oldW, oldH, newW, newH);
                        }

                        canvasRef.current.requestRenderAll();
                        canvasRef.current.calcOffset();
                        window.dispatchEvent(new Event('resize'));

                        canvasRef.current.getObjects().forEach((obj) => {
                            obj.setCoords();
                        });

                        canvasRef.current.renderAll();
                        suppressUnsavedChangesRef.current = false;
                        showSnackbar('Whiteboard successfully loaded!', 'success');
                    }, 600);
                });
            } catch (err) {
                console.error('Failed to auto-load whiteboard:', err);
                suppressUnsavedChangesRef.current = false;
            } finally {
                setIsAutoLoadingWhiteboard(false);
            }
        };

        loadWhiteboard();
    }, []);

    // Auto-load customised handout from My Library (similar to whiteboard pattern)
    // This runs when location changes (navigation) AND handoutData is loaded
    // Key: location.key changes on every navigation, so this runs even if contentId is the same
    useEffect(() => {
        // Only proceed if handoutData has loaded (ensures base content is ready)
        if (!handoutData) return;

        // Open in fresh Customise mode (from search/home page cards — CETRA-738)
        const openInCustomise = localStorage.getItem('openInCustomiseMode');
        if (openInCustomise === 'true') {
            localStorage.removeItem('openInCustomiseMode');
            if (handoutData?.contentData) {
                initializeCustomiseMode(handoutData.contentData['en-UK'], 'en-UK', handoutData.contentId);
                // Match Customise button behavior: customise mode + WB toolbar visible.
                setShowWhiteboard(true);
                setHasUnsavedChanges(false);
            }
            return;
        }

        const customisedHandoutId = localStorage.getItem('loadCustomisedHandout');
        if (!customisedHandoutId || !customisedHandoutStore) {
            setActiveCustomisedHandoutContext(null);
            return;
        }
        const loadCustomisedHandoutInEditMode = localStorage.getItem('loadCustomisedHandoutInEditMode') === 'true';

        // Check if we already loaded this exact combination (prevent duplicate loads on re-renders)
        const lastLoaded = lastLoadedCustomisedHandoutRef.current;
        if (lastLoaded.contentId === contentId && lastLoaded.customisedHandoutId === customisedHandoutId) {
            console.log('[AutoLoad] Skipping duplicate load of CH:', customisedHandoutId, 'for contentId:', contentId);
            return;
        }

        // Remove from localStorage immediately to prevent reloading
        localStorage.removeItem('loadCustomisedHandout');
        localStorage.removeItem('loadCustomisedHandoutInEditMode');

        // CRITICAL: Remove any whiteboard state from localStorage SYNCHRONOUSLY
        // This must happen BEFORE the async function to prevent race condition with whiteboard auto-load
        console.log('[AutoLoad] Clearing localStorage whiteboard state');
        localStorage.removeItem('lastWhiteboardState');

        console.log(
            '[AutoLoad] Detected customised handout to load:',
            customisedHandoutId,
            'for contentId:',
            contentId,
            'location.key:',
            location.key,
        );

        const loadCustomisedHandout = async () => {
            try {
                const waitForCanvasReady = (maxAttempts = 10, delay = 100) =>
                    new Promise((resolve, reject) => {
                        let attempts = 0;
                        const check = () => {
                            const canvas = canvasRef.current;
                            if (canvas && canvas.getWidth() > 10 && canvas.getHeight() > 10) {
                                return resolve(canvas);
                            }
                            attempts += 1;
                            if (attempts >= maxAttempts) {
                                return reject(new Error('Canvas not ready in time'));
                            }
                            setTimeout(check, delay);
                        };
                        check();
                    });

                // IMPORTANT: Clear any existing whiteboard illustration state first
                // This prevents the previous CH's attached whiteboard from persisting
                console.log('[AutoLoad] Clearing previous whiteboard illustration state');
                setWhiteboardIllustration(null);

                // IMPORTANT: Clear any existing canvas to prevent overlapping illustrations
                // when navigating between customised handouts with attached whiteboards
                if (canvasRef.current) {
                    console.log('[AutoLoad] Clearing existing canvas before loading customised handout');
                    canvasRef.current.clear();
                    canvasRef.current.dispose();
                    canvasRef.current = null;
                }

                const ch = await customisedHandoutStore.getCustomisedHandoutById(customisedHandoutId);

                if (!ch) {
                    console.error('[AutoLoad] Customised handout not found:', customisedHandoutId);
                    setActiveCustomisedHandoutContext(null);
                    showSnackbar('Failed to load customised handout', 'error');
                    return;
                }

                if (!ch.effectiveContentData) {
                    console.error('[AutoLoad] Customised handout missing effectiveContentData:', ch);
                    setActiveCustomisedHandoutContext(null);
                    showSnackbar('Invalid customised handout data', 'error');
                    return;
                }

                setActiveCustomisedHandoutContext(ch);
                console.log('[AutoLoad] Loading customised handout content for locale:', ch.locale);

                // Load customised content in edit mode when user clicked Edit in Workspace, else view mode.
                const localeData = ch.effectiveContentData[ch.locale || 'en-UK'];
                if (localeData) {
                    if (loadCustomisedHandoutInEditMode) {
                        loadCustomisedContentForEdit(ch.effectiveContentData, ch.locale || 'en-UK', ch.baseContentId);
                        setIsCustomiseMode(true);
                        setShowWhiteboard(true);
                        setHasUnsavedChanges(false);
                        initialSelectedWhiteboardIdRef.current = ch.attachedWhiteboardId || null;
                    } else {
                        loadCustomisedContentInViewMode(localeData, ch.locale || 'en-UK', ch.baseContentId);
                        initialSelectedWhiteboardIdRef.current = ch.attachedWhiteboardId || null;
                    }

                    // Bug Fix: Set whiteboard reference if attached
                    // The flat PNG will be shown when WB toggle is OFF
                    // If user turns ON WB toggle, it will auto-load the editable canvas
                    if (ch.attachedWhiteboardId) {
                        console.log('[AutoLoad] Setting attached whiteboard reference:', ch.attachedWhiteboardId);
                        const whiteboard = await whiteboardStore.getWhiteboardById(ch.attachedWhiteboardId);
                        if (whiteboard) {
                            setWhiteboardIllustration(whiteboard);

                            // For edited diagrams, always restore the saved diagram variant
                            // in both open(view) and edit flows.
                            if (
                                whiteboard.diagramVariant === 'withLabels' ||
                                whiteboard.diagramVariant === 'noLabels'
                            ) {
                                setShowLabels(whiteboard.diagramVariant === 'withLabels');
                            }

                            if (loadCustomisedHandoutInEditMode) {
                                if (whiteboard.canvasJsonLink) {
                                    try {
                                        const presignedResp = await UserApi.generatePresignedUrls(authStore.idToken, {
                                            contentCategory: 'whiteboard',
                                            files: [{ method: 'get', key: whiteboard.canvasJsonLink }],
                                        });
                                        const presignedUrl = presignedResp.getUrls?.[0]?.presignedUrl;
                                        if (presignedUrl) {
                                            const response = await fetch(presignedUrl);
                                            if (response.ok) {
                                                const jsonData = await response.json();
                                                await waitForCanvasReady();
                                                suppressUnsavedChangesRef.current = true;
                                                canvasRef.current.loadFromJSON(jsonData, () => {
                                                    setTimeout(() => {
                                                        if (!canvasRef.current) {
                                                            suppressUnsavedChangesRef.current = false;
                                                            return;
                                                        }

                                                        if (typeof canvasRef.current.doScaleAndResize === 'function') {
                                                            canvasRef.current.doScaleAndResize();
                                                        }

                                                        const oldW = jsonData.canvasWidth || 0;
                                                        const oldH = jsonData.canvasHeight || 0;
                                                        const newW = canvasRef.current.getWidth();
                                                        const newH = canvasRef.current.getHeight();
                                                        const objects = canvasRef.current.getObjects();

                                                        if (objects.length > 0 && oldW && oldH && newW && newH) {
                                                            scaleLoadedObjects(
                                                                canvasRef.current,
                                                                oldW,
                                                                oldH,
                                                                newW,
                                                                newH,
                                                            );
                                                        }

                                                        canvasRef.current.requestRenderAll();
                                                        canvasRef.current.calcOffset();
                                                        window.dispatchEvent(new Event('resize'));
                                                        canvasRef.current
                                                            .getObjects()
                                                            .forEach((obj) => obj.setCoords());
                                                        canvasRef.current.renderAll();
                                                        suppressUnsavedChangesRef.current = false;
                                                    }, 600);
                                                });
                                            }
                                        }
                                    } catch (wbErr) {
                                        console.error(
                                            '[AutoLoad] Failed to load attached whiteboard canvas in edit mode:',
                                            wbErr,
                                        );
                                        suppressUnsavedChangesRef.current = false;
                                    }
                                }
                            }
                        }
                    }

                    // Store customised handout info in localStorage (similar to whiteboard)
                    localStorage.setItem(
                        'lastCustomisedHandoutState',
                        JSON.stringify({
                            customisedId: ch.customisedId,
                            baseContentId: ch.baseContentId,
                            userId: ch.userId,
                            updatedByFirstName: ch.updatedByFirstName || null,
                            updatedByLastName: ch.updatedByLastName || null,
                            // Include patient details if present (from "My Clinic")
                            patientName: ch.patientName || null,
                            clientSurname: ch.clientSurname || null,
                            patientId: ch.patientId || null,
                            microchipNumber: ch.microchipNumber || null,
                            clinicPatientId: ch.clinicPatientId || null,
                        }),
                    );

                    // Set disclaimer info for customised handout
                    const vetName = `${ch.updatedByFirstName || ''} ${ch.updatedByLastName || ''}`.trim();
                    const clinicName = userDataStore?.userData?.clinicName || '';
                    setDisclaimerVetName(vetName);
                    setDisclaimerClinicName(clinicName);
                    setShowDisclaimer(true);

                    // Mark this CH+contentId combination as loaded to prevent duplicate loads
                    lastLoadedCustomisedHandoutRef.current = { contentId, customisedHandoutId };

                    showSnackbar('Customised handout loaded successfully!', 'success');

                    // Scroll to top of handout
                    if (handoutTopRef.current) {
                        handoutTopRef.current.scrollIntoView({ behavior: 'smooth' });
                    }
                } else {
                    console.error('[AutoLoad] No content data found for locale:', ch.locale);
                    showSnackbar('Failed to load customised handout content', 'error');
                }
            } catch (err) {
                console.error('[AutoLoad] Failed to load customised handout:', err);
                showSnackbar('Failed to load customised handout', 'error');
            }
        };

        loadCustomisedHandout();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key, handoutData]);

    // Track the initial loaded content to detect actual changes
    const initialLoadedContent = useRef(null);

    // Track content changes - only mark as unsaved if content actually changed
    useEffect(() => {
        if (!isCustomiseMode) {
            // When not in edit mode, clear the initial content reference
            initialLoadedContent.current = null;
            return;
        }

        if (isCustomiseMode && editedContentData) {
            // Store initial content when entering edit mode
            if (!initialLoadedContent.current) {
                initialLoadedContent.current = JSON.stringify(editedContentData);
                // Don't mark as unsaved when just entering edit mode
                return;
            }

            // Check if content has actually changed from initial
            const currentContent = JSON.stringify(editedContentData);
            if (currentContent !== initialLoadedContent.current) {
                setHasUnsavedChanges(true);
            }
        }
    }, [editedContentData, isCustomiseMode, setHasUnsavedChanges]);

    // Note: custom mode is cleared on contentId change to avoid StrictMode cleanup wiping restored state.

    // Add Customise toggle handler
    const handleToggleCustomise = (event, newValue) => {
        if (newValue) {
            // Turn ON customise mode — also activates whiteboard
            setShowWhiteboard(true);
            setShowMainIllustration(true);
            if (!isDiagramOnly) {
                // Text editing — only for non-diagram handouts
                if (editedContentData) {
                    // If editedContentData already exists (e.g., loaded CH in view mode), just enable edit mode
                    setIsCustomiseMode(true);
                    setHasUnsavedChanges(false);
                } else if (handoutData?.contentData) {
                    // Otherwise, initialize from original handout data
                    initializeCustomiseMode(handoutData.contentData['en-UK'], 'en-UK', handoutData.contentId);
                    setHasUnsavedChanges(false); // Reset unsaved changes for new session
                }
            } else if (
                selectedWhiteboard?.canvasJsonLink &&
                (!selectedWhiteboard.diagramVariant ||
                    (showLabels && selectedWhiteboard.diagramVariant === 'withLabels') ||
                    (!showLabels && selectedWhiteboard.diagramVariant === 'noLabels'))
            ) {
                // Diagram with a previously saved WB whose variant matches current labels state: restore canvas
                const loadDiagramCanvas = async () => {
                    const waitForCanvasReady = (maxAttempts = 10, delay = 100) =>
                        new Promise((resolve, reject) => {
                            let attempts = 0;
                            const check = () => {
                                if (canvasRef.current) return resolve();
                                if (++attempts >= maxAttempts) return reject(new Error('Canvas not ready'));
                                setTimeout(check, delay);
                            };
                            check();
                        });
                    try {
                        const presignedResp = await UserApi.generatePresignedUrls(authStore.idToken, {
                            contentCategory: 'whiteboard',
                            files: [{ method: 'get', key: selectedWhiteboard.canvasJsonLink }],
                        });
                        const presignedUrl = presignedResp.getUrls?.[0]?.presignedUrl;
                        if (!presignedUrl) throw new Error('No presigned URL');
                        const response = await fetch(presignedUrl);
                        if (!response.ok) throw new Error('Failed to fetch whiteboard JSON');
                        const jsonData = await response.json();
                        await waitForCanvasReady();
                        suppressUnsavedChangesRef.current = true;
                        canvasRef.current.loadFromJSON(jsonData, () => {
                            setTimeout(() => {
                                if (!canvasRef.current) {
                                    suppressUnsavedChangesRef.current = false;
                                    return;
                                }
                                if (typeof canvasRef.current.doScaleAndResize === 'function') {
                                    canvasRef.current.doScaleAndResize();
                                }
                                const oldW = jsonData.canvasWidth || 0;
                                const oldH = jsonData.canvasHeight || 0;
                                const newW = canvasRef.current.getWidth();
                                const newH = canvasRef.current.getHeight();
                                const objects = canvasRef.current.getObjects();
                                if (objects.length > 0 && oldW && oldH && newW && newH) {
                                    scaleLoadedObjects(canvasRef.current, oldW, oldH, newW, newH);
                                }
                                canvasRef.current.requestRenderAll();
                                canvasRef.current.calcOffset();
                                window.dispatchEvent(new Event('resize'));
                                canvasRef.current.getObjects().forEach((obj) => obj.setCoords());
                                canvasRef.current.renderAll();
                                suppressUnsavedChangesRef.current = false;
                            }, 600);
                        });
                    } catch (err) {
                        console.error('[Diagram] Failed to restore canvas for editing:', err);
                        suppressUnsavedChangesRef.current = false;
                    }
                };
                loadDiagramCanvas();
            }
            // Track customise session start
            mixpanel.track('CustomiseSessionStarted', {
                email: userDataStore?.userData?.emailAddress,
                handoutId: handoutData?.contentId,
                handoutTitle: handoutData?.contentName,
            });
        } else {
            // Phase 4: Improved CH toggle-off behavior
            // Turn OFF customise mode
            const exitMode = () => {
                clearCustomiseMode(); // clears editedContentData — restores original text on discard
                setShowWhiteboard(false);
                setHasUnsavedChanges(false);
                setHasActiveWhiteboardDrawings(false);

                // Clear WB canvas on discard — suppress events so object:removed doesn't
                // fire onCanvasModified(true) and re-set hasActiveWhiteboardDrawings back to true
                if (canvasRef.current) {
                    suppressUnsavedChangesRef.current = true;
                    canvasRef.current.clear();
                    suppressUnsavedChangesRef.current = false;
                }
                localStorage.removeItem('lastWhiteboardState');
            };

            // Phase 4: Context-aware warning
            if (hasUnsavedChanges) {
                if (isCombinedMode && hasActiveWhiteboardDrawings) {
                    // In combined mode with WB changes, warn about losing both
                    promptBeforeAction(
                        exitMode,
                        'You have unsaved changes to both the customised handout and whiteboard. Turning off Customise mode will discard all changes. Continue?',
                    );
                } else if (hasActiveWhiteboardDrawings) {
                    // Only WB changes (shouldn't happen, but handle it)
                    promptBeforeAction(
                        exitMode,
                        'You have unsaved whiteboard changes. Turning off Customise mode will discard these changes. Continue?',
                    );
                } else {
                    // Only CH changes
                    promptBeforeAction(exitMode);
                }
            } else {
                exitMode();
            }
        }
    };

    // Unified save handler — saves only what was actually edited
    const hasTextChanges =
        Boolean(isCustomiseMode && initialLoadedContent.current && editedContentData) &&
        JSON.stringify(editedContentData) !== initialLoadedContent.current;
    const hasWhiteboardReplacementChange =
        (selectedWhiteboard?.whiteboardId || null) !== initialSelectedWhiteboardIdRef.current;
    const hasAnyCustomiseChanges = hasTextChanges || hasActiveWhiteboardDrawings || hasWhiteboardReplacementChange;
    const saveHandoutDisabled =
        isCustomiseMode && !hasAnyCustomiseChanges && !activeCustomisedHandoutContext?.customisedId;
    const isFreeOrStudentPlan =
        userDataStore?.subscriptionData?.accountType === 'Free' ||
        userDataStore?.subscriptionData?.accountType === 'Student';
    const isEditedHandoutContextForSaveButton =
        Boolean(isCustomiseMode) ||
        Boolean(showWhiteboard) ||
        Boolean(editedContentData) ||
        Boolean(handoutData?.customisedId) ||
        Boolean(selectedWhiteboard?.whiteboardId) ||
        Boolean(handoutData?.whiteboardId) ||
        Boolean(hasActiveWhiteboardDrawings);
    const saveHandoutLabel = isEditedHandoutContextForSaveButton ? 'Save handout' : 'Save to patient';

    const openRegularSaveHandoutModal = () => {
        // Free and Student users should see the Professional upgrade CTA before the backend patient-save validation.
        if (isFreeOrStudentPlan) {
            setUpgradeCTAOpen(true);
            return;
        }
        setIsSaveRegularHandoutModalOpen(true);
    };

    const handleSaveFromBar = () => {
        const isEditedHandoutContext =
            Boolean(editedContentData) ||
            Boolean(handoutData?.customisedId) ||
            Boolean(selectedWhiteboard?.whiteboardId) ||
            Boolean(handoutData?.whiteboardId);

        if (isDiagramOnly) {
            // Diagram-only:
            // - in whiteboard/customise context -> save whiteboard
            // - in plain view context -> allow regular "Saved handout" flow
            if (showWhiteboard) {
                handleSave();
            } else {
                openRegularSaveHandoutModal();
            }
            return;
        }
        if (isCustomiseMode && !hasAnyCustomiseChanges && !activeCustomisedHandoutContext?.customisedId) {
            showSnackbar('No changes to save', 'warning');
            return;
        }
        if (hasUnsavedChanges) {
            // CH modal handles WB too when in combined mode
            if (isCustomiseMode) {
                handleSaveCustomisedHandout();
            } else {
                handleSave();
            }
        } else if (isCustomiseMode && (hasTextChanges || hasWhiteboardReplacementChange)) {
            handleSaveCustomisedHandout();
        } else if (hasActiveWhiteboardDrawings) {
            handleSave();
        } else if (isEditedHandoutContext) {
            // Never allow non-customised save flow from edited contexts.
            // If actively editing a standalone whiteboard route to WB modal, otherwise CH modal.
            if (showWhiteboard || handoutData?.whiteboardId) {
                handleSave();
            } else {
                handleSaveCustomisedHandout();
            }
        } else {
            openRegularSaveHandoutModal();
        }
    };

    const applyWhiteboardColours = (entryOrId) => {
        // 1) Use colours passed directly
        if (entryOrId?.lastUsedColours) {
            setLastUsedColours({
                pencil: entryOrId.lastUsedColours.pencil || [],
                spray: entryOrId.lastUsedColours.spray || [],
            });
            return true;
        }

        // 2) Try store
        const wbId = typeof entryOrId === 'string' ? entryOrId : entryOrId?.whiteboardId;
        const fromStore = wbId ? whiteboardStore?.whiteboardData?.[wbId] : null;

        if (fromStore?.lastUsedColours) {
            setLastUsedColours({
                pencil: fromStore.lastUsedColours.pencil || [],
                spray: fromStore.lastUsedColours.spray || [],
            });
            return true;
        }

        // 3) Fall back to user colours only if we haven't set anything yet
        setLastUsedColours((prev) => {
            const alreadySet = (prev?.pencil?.length || 0) > 0 || (prev?.spray?.length || 0) > 0;
            if (alreadySet) return prev;
            const seed = userDataStore?.lastUsedColours || userDataStore?.userData?.lastUsedColours || {};
            return { pencil: seed.pencil || [], spray: seed.spray || [] };
        });
        return false;
    };

    const updateRecentHandouts = (newContentId) => {
        const recentHandouts = JSON.parse(localStorage.getItem('recentHandouts')) || [];
        const updatedRecentHandouts = [newContentId, ...recentHandouts.filter((id) => id !== newContentId)].slice(
            0,
            MAX_RECENT_HANDOUTS,
        );
        localStorage.setItem('recentHandouts', JSON.stringify(updatedRecentHandouts));
        // console.log(`Updated recent handouts: ${updatedRecentHandouts}`);
    };

    const toggleAuthorsAndReferences = () => {
        // Authors is an overlay — it does not affect the editing session,
        // so never prompt for unsaved changes here
        setShowAuthorsAndReferences((prev) => !prev);
    };

    useEffect(() => {
        if (!isTabletOrMobile || !isCustomiseMode || !showWhiteboard) return;
        const mainIllustration = document.getElementById('main-illustration-img');
        if (mainIllustration) {
            mainIllustration.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [isTabletOrMobile, isCustomiseMode, showWhiteboard]);

    useEffect(() => {
        if (!isTabletOrMobile || !showWhiteboard) {
            setIsMobileToolbarVisible(true);
            return;
        }

        const updateVisibility = () => {
            const illustration = document.getElementById('main-illustration-img');
            if (!illustration) {
                setIsMobileToolbarVisible(true);
                return;
            }

            const rect = illustration.getBoundingClientRect();
            const inViewport = rect.bottom > 0 && rect.top < window.innerHeight;
            setIsMobileToolbarVisible(inViewport);
        };

        updateVisibility();
        window.addEventListener('scroll', updateVisibility, { passive: true });
        window.addEventListener('resize', updateVisibility);
        return () => {
            window.removeEventListener('scroll', updateVisibility);
            window.removeEventListener('resize', updateVisibility);
        };
    }, [isTabletOrMobile, showWhiteboard]);

    if (!handoutData) {
        return (
            <Box sx={{ my: 2 }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    const handleSave = () => {
        // Check if user is on Free plan - show upgrade dialog instead of save modal
        if (userDataStore?.subscriptionData?.accountType === 'Free') {
            // Store whiteboard data temporarily before showing upgrade dialog
            if (canvasRef.current) {
                try {
                    const rawJson = canvasRef.current.toJSON();
                    rawJson.canvasWidth = canvasRef.current.getWidth();
                    rawJson.canvasHeight = canvasRef.current.getHeight();
                    const canvasJson = JSON.stringify(rawJson);

                    // Store in localStorage with timestamp
                    localStorage.setItem(
                        'pendingWhiteboardSave',
                        JSON.stringify({
                            canvasData: canvasJson,
                            handoutId: handoutData.contentId,
                            handoutContentName: handoutData.contentName,
                            timestamp: Date.now(),
                            showLabels,
                            lastUsedColours,
                        }),
                    );
                    setPendingUpgradeHandoutId(handoutData.contentId);
                    sessionStorage.removeItem('pendingWhiteboardPromptDismissedAt');
                    console.log('Stored whiteboard data for Free plan upgrade flow');
                } catch (error) {
                    console.error('Error storing whiteboard data:', error);
                }
            }
            setUpgradeCTAOpen(true);
            return;
        }

        setIsSaveModalOpen(true); // opens the modal when Save clicked
    };

    const handleLoad = async (entry) => {
        const vetName = `${entry.updatedByFirstName || ''} ${entry.updatedByLastName || ''}`.trim();
        const clinicName = userDataStore?.userData?.clinicName || '';

        setDisclaimerVetName(vetName);
        setDisclaimerClinicName(clinicName);
        setShowDisclaimer(true);

        applyWhiteboardColours(entry);
    };

    const openLoadModal = () => {
        // Check if user is on Free or Student plan - show upgrade dialog instead of load modal
        if (
            userDataStore?.subscriptionData?.accountType === 'Free' ||
            userDataStore?.subscriptionData?.accountType === 'Student'
        ) {
            try {
                const pendingSave = {
                    editedContentData: getEditedContentData(),
                    handoutId: handoutData.contentId,
                    handoutContentName: handoutData.contentName,
                    timestamp: Date.now(),
                    selectedWhiteboard: selectedWhiteboard
                        ? {
                              whiteboardId: selectedWhiteboard.whiteboardId,
                              canvasImageLink: selectedWhiteboard.canvasImageLink,
                              eTag: selectedWhiteboard.eTag || null,
                              diagramVariant: selectedWhiteboard.diagramVariant || null,
                          }
                        : null,
                };
                localStorage.setItem('pendingCustomisedHandoutSave', JSON.stringify(pendingSave));
                setPendingUpgradeHandoutId(handoutData.contentId);
                sessionStorage.removeItem('pendingCustomisedPromptDismissedAt');
                console.log('Stored customised handout data for upgrade flow');
            } catch (error) {
                console.error('Error storing customised handout data:', error);
            }
            setUpgradeCTAOpen(true);
            return;
        }

        setIsLoadModalOpen(true);
    };

    const handleReset = () => {
        if (canvasRef.current) {
            suppressUnsavedChangesRef.current = true;
            canvasRef.current.clear();
            suppressUnsavedChangesRef.current = false;
            localStorage.removeItem('lastWhiteboardState');
            console.warn('Whiteboard data cleared from localStorage by reset');
            // Keep unsaved flag true if customise text is still changed.
            const hasUnsavedCustomiseChanges =
                isCustomiseMode &&
                initialLoadedContent.current &&
                editedContentData &&
                JSON.stringify(editedContentData) !== initialLoadedContent.current;
            setHasUnsavedChanges(!!hasUnsavedCustomiseChanges);
            setHasActiveWhiteboardDrawings(false);
        }
    };

    // Phase 1: Track canvas modifications for combined mode
    const handleCanvasModified = (hasChanges) => {
        setHasActiveWhiteboardDrawings(hasChanges);
        // Also set canvas ref in context when canvas is first modified
        if (hasChanges && canvasRef.current && !activeCanvasRef) {
            setActiveCanvasRef(canvasRef);
        }
    };

    // After user saves, reset the unsaved state
    const handleWhiteboardSaved = () => {
        // for the dialog
        onCancelNavigation();
        setHasUnsavedChanges(false);
        setIsSaveModalOpen(false);
        localStorage.removeItem('pendingWhiteboardSave');
        localStorage.removeItem('pendingCustomisedHandoutSave');
        localStorage.removeItem('pendingCombinedHandoutSave');
        clearPendingUpgradeHandoutId();

        // for disclaimer
        const vetName = `${userDataStore?.userData?.firstName || ''} ${userDataStore?.userData?.lastName || ''}`.trim();
        const clinicName = userDataStore?.userData?.clinicName || '';

        setDisclaimerVetName(vetName);
        setDisclaimerClinicName(clinicName);
        setShowDisclaimer(true);
    };

    // rgb to hex
    const normalizeHex = (c) => {
        if (!c || typeof c !== 'string') return null;
        if (c.startsWith('rgba')) return null; // never store highlighter’s rgba
        if (c[0] === '#' && c.length === 4) {
            // #rgb → #rrggbb
            const r = c[1],
                g = c[2],
                b = c[3];
            return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
        }
        return c.toLowerCase();
    };

    const recordColourUse = (usedColour, tool) => {
        if (!['pencil', 'spray'].includes(tool)) return;

        const hex = normalizeHex(usedColour);
        if (!hex) return;

        setLastUsedColours((prev) => {
            const list = prev[tool] || [];
            const next = [hex, ...list.filter((x) => x.toLowerCase() !== hex)].slice(0, 6);
            return { ...prev, [tool]: next };
        });
    };

    const handleSaveCustomisedHandout = () => {
        console.log('[Customise Save] Triggered', {
            isCustomiseMode,
            hasEditedContent: !!editedContentData,
            accountType: userDataStore?.subscriptionData?.accountType,
            contentId: handoutData?.contentId,
        });
        if (!editedContentData) {
            console.warn('[Customise Save] No changes to save', {
                isCustomiseMode,
                hasEditedContent: !!editedContentData,
            });
            showSnackbar('No changes to save', 'warning');
            return;
        }

        // Check if user is on Free or Student plan - show upgrade dialog instead of save modal
        if (
            userDataStore?.subscriptionData?.accountType === 'Free' ||
            userDataStore?.subscriptionData?.accountType === 'Student'
        ) {
            try {
                const baseSave = {
                    editedContentData: getEditedContentData(),
                    handoutId: handoutData.contentId,
                    handoutContentName: handoutData.contentName,
                    timestamp: Date.now(),
                    selectedWhiteboard: selectedWhiteboard
                        ? {
                              whiteboardId: selectedWhiteboard.whiteboardId,
                              canvasImageLink: selectedWhiteboard.canvasImageLink,
                              eTag: selectedWhiteboard.eTag || null,
                              diagramVariant: selectedWhiteboard.diagramVariant || null,
                          }
                        : null,
                };

                const hasCombinedSave = isCombinedMode && hasActiveWhiteboardDrawings && canvasRef?.current;

                if (hasCombinedSave) {
                    const rawJson = canvasRef.current.toJSON();
                    rawJson.canvasWidth = canvasRef.current.getWidth();
                    rawJson.canvasHeight = canvasRef.current.getHeight();
                    const canvasJson = JSON.stringify(rawJson);

                    const combinedSave = {
                        ...baseSave,
                        whiteboard: {
                            canvasData: canvasJson,
                            showLabels,
                            lastUsedColours,
                        },
                    };

                    localStorage.setItem('pendingCombinedHandoutSave', JSON.stringify(combinedSave));
                    localStorage.removeItem('pendingCustomisedHandoutSave');
                    localStorage.removeItem('pendingWhiteboardSave');
                    setPendingUpgradeHandoutId(handoutData.contentId);
                    sessionStorage.removeItem('pendingCombinedPromptDismissedAt');
                    console.log('[Customise Save] Stored pendingCombinedHandoutSave', combinedSave);
                } else {
                    localStorage.setItem('pendingCustomisedHandoutSave', JSON.stringify(baseSave));
                    setPendingUpgradeHandoutId(handoutData.contentId);
                    sessionStorage.removeItem('pendingCustomisedPromptDismissedAt');
                    console.log('[Customise Save] Stored pendingCustomisedHandoutSave', baseSave);
                }
            } catch (error) {
                console.error('[Customise Save] Error storing pending save:', error);
            }
            setUpgradeCTAOpen(true);
            return;
        }

        setIsSaveCustomisedHandoutModalOpen(true);
    };

    const handleSaveCustomisedHandoutComplete = (savedWhiteboard) => {
        setIsSaveCustomisedHandoutModalOpen(false);
        setHasUnsavedChanges(false);
        exitCustomiseMode();
        setShowWhiteboard(false);
        if (savedWhiteboard) setWhiteboardIllustration(savedWhiteboard);
        initialSelectedWhiteboardIdRef.current = savedWhiteboard?.whiteboardId || null;
        setIsToolbarExpanded(false);
        // Mark as loaded customised handout so icon appears
        markAsLoadedCustomisedHandout();
        localStorage.removeItem('pendingCustomisedHandoutSave');
        localStorage.removeItem('pendingWhiteboardSave');
        localStorage.removeItem('pendingCombinedHandoutSave');
        // Clear whiteboard session state so navigating back to the original handout
        // from search does not trigger the whiteboard auto-load
        localStorage.removeItem('lastWhiteboardState');
        clearPendingUpgradeHandoutId();
    };

    const createLogoFileObject = (file) => {
        const extension = file.name.split('.').pop();
        const baseKey = `${userDataStore.userId}/profile-image`;
        return {
            key: `${baseKey}.${extension}`,
            fileContentType: file.type,
            fileSize: file.size,
        };
    };

    const validateLogoFile = async (file) =>
        new Promise((resolve, reject) => {
            if (!file) return reject(new Error('No file selected.'));
            if (file.size > 2 * 1024 * 1024) {
                return reject(new Error('File size exceeds the allowed maximum of 2MB.'));
            }
            const objectURL = URL.createObjectURL(file);
            const image = new Image();
            image.src = objectURL;
            image.onload = () => {
                if (image.width > 700 || image.height > 700) {
                    URL.revokeObjectURL(objectURL);
                    return reject(new Error('Image dimensions must not exceed 700x700 pixels.'));
                }
                URL.revokeObjectURL(objectURL);
                resolve(true);
            };
            image.onerror = () => {
                URL.revokeObjectURL(objectURL);
                reject(new Error('Could not read image file.'));
            };
        });

    const handleUploadLogoClick = () => {
        const accountType = userDataStore?.subscriptionData?.accountType || '';
        if (!accountType.includes('Clinic')) {
            setUpgradeClinicCTAOpen(true);
            return;
        }
        setLogoModalOpen(true);
    };

    const handleSelectLogoFile = async (file) => {
        try {
            await validateLogoFile(file);
            setSelectedLogoFile(file);
        } catch (error) {
            showSnackbar(error.message, 'error');
            setSelectedLogoFile(null);
        }
    };

    const handleSaveLogo = async () => {
        if (!selectedLogoFile) return;
        setIsSavingLogo(true);
        try {
            const fileObject = createLogoFileObject(selectedLogoFile);
            const response = await UserApi.createPresignedImagePutUrl(authStore.idToken, fileObject);
            if (!response?.putUrls?.length) throw new Error('Failed to get upload URL.');
            const { presignedUrl, fields } = response.putUrls[0];
            const uploaded = await UploadUtils.uploadFile(fields, selectedLogoFile, presignedUrl);
            if (!uploaded) throw new Error('Upload failed.');

            const payload = {
                firstName: userDataStore?.userData?.firstName || '',
                lastName: userDataStore?.userData?.lastName || '',
                emailAddress: userDataStore?.userData?.emailAddress || '',
                mobile: userDataStore?.userData?.mobile || '',
                jobTitle: userDataStore?.userData?.jobTitle || '',
                yearsExperience: userDataStore?.userData?.yearsExperience || '',
                clinicName: userDataStore?.userData?.clinicName || '',
                clinicNumber: userDataStore?.userData?.clinicNumber || '',
                clinicSize: userDataStore?.userData?.clinicSize || '',
                clinicType: userDataStore?.userData?.clinicType || '',
                showLogoInHandouts: true,
                clinicImageId: fileObject.key,
            };
            await UserApi.updateClinicianData(authStore.idToken, payload);

            const getPayload = {
                files: [{ method: 'get', key: fileObject.key }],
                contentCategory: 'clinic_image',
            };
            const clinicImageURL = await UserApi.generatePresignedUrls(authStore.idToken, getPayload);
            if (clinicImageURL?.getUrls?.[0]?.presignedUrl) {
                userDataStore.setUserData({
                    clinicImageId: clinicImageURL.getUrls[0].presignedUrl,
                    showLogoInHandouts: true,
                });
            }
            showSnackbar('Practice logo uploaded successfully.', 'success');
            setLogoModalOpen(false);
            setSelectedLogoFile(null);
        } catch (error) {
            console.error('Logo upload failed:', error);
            showSnackbar(error.message || 'Failed to upload logo.', 'error');
        } finally {
            setIsSavingLogo(false);
        }
    };

    return (
        <>
            {isAutoLoadingWhiteboard && <BlockingSpinner open={true} />}
            <HandoutActions
                handoutData={handoutData}
                onToggleAuthorsAndReferences={toggleAuthorsAndReferences}
                showLabels={showLabels}
                show3DModel={show3DModel}
                showMainIllustration={showMainIllustration}
                showTutorialVideo={showTutorialVideo} // we do not have this yet
                isDiagramOnly={isDiagramOnly}
                is3DModelOnly={is3DModelOnly}
                showWhiteboard={showWhiteboard}
                customisedHandoutData={editedContentData ? { hasCustomisedContent: true } : null}
                isCustomiseMode={isCustomiseMode}
                onToggleCustomise={handleToggleCustomise}
                onSaveHandout={handleSaveFromBar}
                saveHandoutDisabled={saveHandoutDisabled}
                saveHandoutLabel={saveHandoutLabel}
            />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    pt: isTabletOrMobile ? 0 : '40px',
                    pb: !isTabletOrMobile && showWhiteboard ? '180px' : 0,
                    position: 'relative',
                    backgroundColor: '#F0F0FC',
                }}
            >
                {/* Toolbar - desktop */}
                {!isTabletOrMobile && showWhiteboard && (
                    <Box
                        className="whiteboard-toolbar-position"
                        sx={{
                            position: 'fixed',
                            left: '50%',
                            bottom: '24px',
                            transform: 'translateX(-50%)',
                            zIndex: 1200,
                            opacity: showPrompt ? 0.35 : 1,
                            pointerEvents: showPrompt ? 'none' : 'auto',
                        }}
                    >
                        <WhiteboardToolbar
                            activeTool={activeTool}
                            setActiveTool={(tool) => {
                                setActiveTool(tool);
                                setIsToolbarExpanded(true); // expand on tool click
                            }}
                            color={activeColor}
                            setColor={setColor}
                            thickness={thickness}
                            setThickness={setThickness}
                            onSave={handleSave}
                            onLoad={openLoadModal}
                            onReset={handleReset}
                            hasUnsavedChanges={hasUnsavedChanges}
                            isExpanded={isToolbarExpanded}
                            setIsExpanded={setIsToolbarExpanded}
                            recentColours={lastUsedColours[activeTool] || []}
                            onPickRecentColour={(c) => setColor(c)}
                            showTextHelp={true}
                            isCombinedMode={isCombinedMode || isDiagramOnly} // hide toolbar Save for diagrams — use top-bar Save handout instead
                            isMobile={false}
                        />
                    </Box>
                )}

                {/* Toolbar - mobile */}
                {isTabletOrMobile && showWhiteboard && (
                    <Box
                        sx={{
                            position: 'fixed',
                            left: '50%',
                            bottom: '98px',
                            transform: 'translateX(-50%)',
                            zIndex: 1200,
                            opacity: showPrompt || !isMobileToolbarVisible ? 0 : 1,
                            visibility: isMobileToolbarVisible ? 'visible' : 'hidden',
                            pointerEvents: showPrompt || !isMobileToolbarVisible ? 'none' : 'auto',
                            transition: 'opacity 0.2s ease',
                        }}
                    >
                        <WhiteboardToolbar
                            activeTool={activeTool}
                            setActiveTool={setActiveTool}
                            color={color}
                            setColor={setColor}
                            thickness={thickness}
                            setThickness={setThickness}
                            onSave={handleSave}
                            onLoad={openLoadModal}
                            onReset={handleReset}
                            hasUnsavedChanges={hasUnsavedChanges}
                            isExpanded={true}
                            setIsExpanded={() => {}}
                            recentColours={lastUsedColours[activeTool] || []}
                            onPickRecentColour={(c) => setColor(c)}
                            showTextHelp={false}
                            isCombinedMode={isCombinedMode || isDiagramOnly}
                            isMobile={true}
                        />
                    </Box>
                )}

                <SaveWhiteboardModal
                    open={isSaveModalOpen}
                    onClose={() => setIsSaveModalOpen(false)}
                    canvasRef={canvasRef}
                    handoutData={handoutData}
                    // Pass to know when the user has actually saved
                    onSaved={handleWhiteboardSaved}
                    hasUnsavedChanges={hasUnsavedChanges}
                    showLabels={showLabels}
                    lastUsedColours={lastUsedColours}
                    sourceCustomisedHandout={activeCustomisedHandoutContext}
                />

                <LoadWhiteboardModal
                    open={isLoadModalOpen}
                    onClose={() => setIsLoadModalOpen(false)}
                    onOpenFile={handleLoad}
                    handoutData={handoutData}
                    canvasRef={canvasRef}
                    onSetShowLabels={(val) => setShowLabels(val)}
                    suppressUnsavedChangesRef={suppressUnsavedChangesRef}
                />

                <SaveCustomisedHandoutModal
                    open={isSaveCustomisedHandoutModalOpen}
                    onClose={() => setIsSaveCustomisedHandoutModalOpen(false)}
                    handoutData={handoutData}
                    editedContentData={getEditedContentData()}
                    onSaved={handleSaveCustomisedHandoutComplete}
                    isCombinedMode={isCombinedMode} // Phase 1: Combined mode flag
                    hasActiveWhiteboardDrawings={hasActiveWhiteboardDrawings} // Phase 1: WB changes flag
                    activeCanvasRef={activeCanvasRef} // Phase 1: Canvas reference
                    canvasRef={canvasRef} // Phase 1: Canvas ref for WB save logic
                    sourceCustomisedHandout={activeCustomisedHandoutContext}
                    hasCustomisedChanges={hasAnyCustomiseChanges}
                />
                <SaveRegularHandoutModal
                    open={isSaveRegularHandoutModalOpen}
                    onClose={() => setIsSaveRegularHandoutModalOpen(false)}
                    handoutData={handoutData}
                    existingSavedHandout={activeSavedHandoutContext}
                    onSaved={() => {
                        setIsSaveRegularHandoutModalOpen(false);
                    }}
                />

                <CTAUpgradeDialog
                    open={upgradeCTAOpen}
                    handleClose={() => {
                        setUpgradeCTAOpen(false);
                    }}
                    onConfirm={() => navigate('/settings/manage-subscription')}
                    title="Unlock saving, sharing and more with just one click!"
                    message="You've reached a feature that's beyond your current plan. Upgrade today to enjoy full access!"
                    buttonText="Upgrade plan"
                    benefits={[
                        'Unlimited digital sharing of all handouts',
                        'Unlimited digital sharing of all anatomy diagrams',
                        'Personalise with your clinic details ',
                    ]}
                    imageUrl={getCTAUpgradeImageUrl('professional')}
                />

                {/* Handout Content */}
                <Container
                    component={'section'}
                    maxWidth="md"
                    sx={{
                        width: '100%',
                        overflowY: 'auto',
                        backgroundColor: 'white',
                        borderRadius: isTabletOrMobile ? 0 : '30px 30px 0 0',
                    }}
                    className="handout-container"
                >
                    <Box sx={{ my: 2 }} component={'section'} ref={handoutTopRef}>
                        <DisplayHandout
                            handoutData={handoutData}
                            showLabels={showLabels}
                            setShowLabels={setShowLabels}
                            show3DModel={show3DModel}
                            showMainIllustration={showMainIllustration}
                            showTutorialVideo={showTutorialVideo}
                            showWhiteboard={showWhiteboard}
                            activeTool={activeTool}
                            color={color}
                            thickness={thickness}
                            canvasRef={canvasRef}
                            onUnsavedChanges={setHasUnsavedChanges}
                            setToolbarExpanded={isTabletOrMobile ? () => {} : setIsToolbarExpanded}
                            showDisclaimer={
                                showDisclaimer && userDataStore?.subscriptionData?.accountType !== 'Student'
                            }
                            vetName={disclaimerVetName}
                            clinicName={disclaimerClinicName}
                            suppressUnsavedChangesRef={suppressUnsavedChangesRef}
                            onColorUsed={recordColourUse}
                            onCanvasModified={handleCanvasModified}
                            hasActiveWhiteboardDrawings={hasActiveWhiteboardDrawings}
                            activeCustomisedHandoutContext={activeCustomisedHandoutContext}
                            onToggle3DModel={!is3DModelOnly ? () => setShow3DModel((v) => !v) : undefined}
                            onToggleDiagram={!isDiagramOnly ? () => setShowMainIllustration((v) => !v) : undefined}
                            onToggleTutorialVideo={() => setShowTutorialVideo((v) => !v)}
                            onUploadLogoClick={handleUploadLogoClick}
                        />
                    </Box>
                </Container>
            </Box>

            <UploadPracticeLogoModal
                open={logoModalOpen}
                onClose={() => {
                    setLogoModalOpen(false);
                    setSelectedLogoFile(null);
                }}
                onSelectFile={handleSelectLogoFile}
                selectedFile={selectedLogoFile}
                onSave={handleSaveLogo}
                isSaving={isSavingLogo}
            />

            <AuthorsAndReferences
                handoutData={handoutData}
                open={showAuthorsAndReferences}
                onToggleAuthorsAndReferences={toggleAuthorsAndReferences}
            />

            <CTAUpgradeDialog
                open={upgradeClinicCTAOpen}
                handleClose={() => setUpgradeClinicCTAOpen(false)}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Upgrade now for clinic-wide access and advanced features!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to add more users and enjoy full access to our features!"
                buttonText="Upgrade plan"
                benefits={[
                    'Unlimited digital sharing of all handouts & anatomy diagrams',
                    'Bundling of handouts and diagrams for seamless sharing',
                    'Personalise with your clinic logo',
                ]}
                imageUrl={getCTAUpgradeImageUrl('clinic')}
            />
        </>
    );
}

export default HandoutViewer;
